// src/components/PointsCalculator.js
import React, { useState, useEffect } from 'react';
import staticApi from '../services/staticApi';
import { calculateAllPoints, validateInputs } from '../services/pointsCalculator';
import { saveShowData, loadShowData } from '../utils/showDataCache';
import { LoadingSpinner, ErrorAlert, InfoCard } from './shared/FormComponents';

const PointsCalculator = () => {
  const [state, setState] = useState({
    // Form data
    years: [], selectedYear: new Date().getFullYear(),
    states: [], selectedState: '', stateSearchTerm: '', isStateDropdownOpen: false,
    breeds: [], selectedBreed: '', breedSearchTerm: '', isBreedDropdownOpen: false,
    classDogs: 2, classBitches: 2, championDogs: 0, championBitches: 0,
    
    // Schedule data
    dogSchedule: [], bitchSchedule: [], divisionNumber: null,
    
    // Results
    calculationResults: null, validationErrors: [],
    
    // UI state
    loading: true, error: null, filteredStates: [], filteredBreeds: []
  });

  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  // Data fetching
  const fetchInitialData = async () => {
    try {
      updateState({ loading: true, error: null });
      const [yearsData, breedsData, statesByYearData] = await Promise.all([
        staticApi.getYears(), staticApi.getBreeds(), staticApi.getStatesByYear()
      ]);
      
      const currentYear = new Date().getFullYear();
      const availableYear = yearsData.find(y => y.year === currentYear) || yearsData[0];
      const yearStates = statesByYearData[availableYear.year.toString()] || [];

      // Try to load from cache
      const cached = loadShowData();
      let initialYear = availableYear.year;
      let initialState = '';
      let initialBreed = '';
      let initialStateSearch = '';
      let initialBreedSearch = '';

      if (cached) {
        // Validate cached data against current dataset
        if (yearsData.find(y => y.year === cached.year)) {
          initialYear = cached.year;
        }
        const cachedYearStates = statesByYearData[initialYear.toString()] || [];
        if (cachedYearStates.includes(cached.state)) {
          initialState = cached.state;
          initialStateSearch = cached.state;
        }
        if (breedsData.includes(cached.breed)) {
          initialBreed = cached.breed;
          initialBreedSearch = cached.breed;
        }
      }

      updateState({
        years: yearsData, breeds: breedsData, filteredBreeds: breedsData,
        selectedYear: initialYear, states: statesByYearData[initialYear.toString()] || [],
        filteredStates: statesByYearData[initialYear.toString()] || [],
        selectedState: initialState, selectedBreed: initialBreed,
        stateSearchTerm: initialStateSearch, breedSearchTerm: initialBreedSearch,
        loading: false
      });
    } catch (error) {
      updateState({ error: 'Failed to load calculator data', loading: false });
    }
  };

  const fetchSchedule = async () => {
    if (!state.selectedYear || !state.selectedState || !state.selectedBreed) return;
    
    try {
      const data = await staticApi.getSchedule(state.selectedState, state.selectedBreed, state.selectedYear);
      
      updateState({
        dogSchedule: data.dog_points || [],
        bitchSchedule: data.bitch_points || [],
        divisionNumber: data.division_number
      });
    } catch (error) {
      updateState({ error: 'Failed to load points schedule' });
    }
  };

  // Initialize
  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { fetchSchedule(); }, [state.selectedYear, state.selectedState, state.selectedBreed]);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (state.dogSchedule.length > 0 && state.bitchSchedule.length > 0) {
      const results = calculateAllPoints({
        dogSchedule: state.dogSchedule,
        bitchSchedule: state.bitchSchedule,
        classDogs: state.classDogs,
        classBitches: state.classBitches,
        championDogs: state.championDogs,
        championBitches: state.championBitches
      });
      
      updateState({ calculationResults: results });
    }
  }, [
    state.dogSchedule,
    state.bitchSchedule,
    state.classDogs,
    state.classBitches,
    state.championDogs,
    state.championBitches
  ]);

  // Save selections to cache
  useEffect(() => {
    if (state.selectedYear && state.selectedState && state.selectedBreed) {
      saveShowData({
        year: state.selectedYear,
        state: state.selectedState,
        breed: state.selectedBreed
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedYear, state.selectedState, state.selectedBreed]);

  // Handlers
  const calculateResults = () => {
    const errors = validateInputs(state);
    if (errors.length > 0) {
      updateState({ validationErrors: errors, calculationResults: null });
      return;
    }
    
    const results = calculateAllPoints({
      dogSchedule: state.dogSchedule,
      bitchSchedule: state.bitchSchedule,
      classDogs: state.classDogs,
      classBitches: state.classBitches,
      championDogs: state.championDogs,
      championBitches: state.championBitches
    });
    
    updateState({ 
      calculationResults: results, 
      validationErrors: [] 
    });
  };

  const handleYearChange = async (e) => {
    const year = parseInt(e.target.value);
    updateState({ selectedYear: year, selectedState: '', selectedBreed: '' });
    
    try {
      const statesByYear = await staticApi.getStatesByYear();
      const yearStates = statesByYear[year.toString()] || [];
      updateState({ states: yearStates, filteredStates: yearStates });
    } catch (error) {
      updateState({ error: 'Failed to load states' });
    }
  };

  const handleStateSelect = (stateName) => {
    updateState({ 
      selectedState: stateName, 
      isStateDropdownOpen: false, 
      stateSearchTerm: stateName 
    });
  };

  const handleBreedSelect = (breedName) => {
    updateState({ 
      selectedBreed: breedName, 
      isBreedDropdownOpen: false, 
      breedSearchTerm: breedName 
    });
  };

  const handleStateSearch = (e) => {
    const term = e.target.value;
    const filtered = state.states.filter(s => s.toLowerCase().includes(term.toLowerCase()));
    updateState({ 
      stateSearchTerm: term, 
      filteredStates: filtered,
      isStateDropdownOpen: true 
    });
  };

  const handleBreedSearch = (e) => {
    const term = e.target.value;
    const filtered = state.breeds.filter(b => b.toLowerCase().includes(term.toLowerCase()));
    updateState({ 
      breedSearchTerm: term, 
      filteredBreeds: filtered,
      isBreedDropdownOpen: true 
    });
  };

  const handleNumberChange = (field) => (e) => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    updateState({ [field]: value });
  };

  if (state.loading) return <LoadingSpinner message="Loading calculator data..." />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Points Calculator
          </h1>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            Calculate championship and grand champion points for your dog show entries
          </p>
        </div>

        {state.error && <ErrorAlert error={state.error} />}
        {state.validationErrors.length > 0 && <ErrorAlert error={state.validationErrors.join('. ')} />}

        {/* Form Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Show Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Show Year</label>
              <select 
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm sm:text-base"
                value={state.selectedYear} 
                onChange={handleYearChange}
              >
                {state.years.map(y => (
                  <option key={y.year} value={y.year}>{y.year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Show State</label>
              <div className="relative">
                <input
                  type="text"
                  value={state.stateSearchTerm}
                  onChange={handleStateSearch}
                  onFocus={() => updateState({ isStateDropdownOpen: true })}
                  onBlur={() => setTimeout(() => updateState({ isStateDropdownOpen: false }), 200)}
                  placeholder="Search and select state..."
                  disabled={!state.selectedYear}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {state.isStateDropdownOpen && state.filteredStates.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {state.filteredStates.slice(0, 100).map((option, index) => (
                      <button
                        key={option}
                        onClick={() => handleStateSelect(option)}
                        className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-left text-sm sm:text-base hover:bg-purple-50 focus:bg-purple-50 focus:outline-none transition-colors duration-150 ${
                          index === 0 ? 'rounded-t-xl' : ''
                        } ${
                          index === Math.min(state.filteredStates.length - 1, 99) ? 'rounded-b-xl' : ''
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {state.divisionNumber && (
                <p className="text-xs text-gray-500 mt-1">Division {state.divisionNumber}</p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
            <div className="relative">
              <input
                type="text"
                value={state.breedSearchTerm}
                onChange={handleBreedSearch}
                onFocus={() => updateState({ isBreedDropdownOpen: true })}
                onBlur={() => setTimeout(() => updateState({ isBreedDropdownOpen: false }), 200)}
                placeholder="Search and select breed..."
                disabled={!state.selectedState}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {state.isBreedDropdownOpen && state.filteredBreeds.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {state.filteredBreeds.slice(0, 100).map((option, index) => (
                    <button
                      key={option}
                      onClick={() => handleBreedSelect(option)}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-left text-sm sm:text-base hover:bg-purple-50 focus:bg-purple-50 focus:outline-none transition-colors duration-150 ${
                        index === 0 ? 'rounded-t-xl' : ''
                      } ${
                        index === Math.min(state.filteredBreeds.length - 1, 99) ? 'rounded-b-xl' : ''
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {state.filteredBreeds.length === 0 && state.breedSearchTerm && (
              <p className="text-xs text-red-500 mt-1">No breeds found</p>
            )}
          </div>

          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Competition Numbers</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Class Dogs</label>
              <input
                type="number"
                value={state.classDogs}
                onChange={handleNumberChange('classDogs')}
                min={0}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Class Bitches</label>
              <input
                type="number"
                value={state.classBitches}
                onChange={handleNumberChange('classBitches')}
                min={0}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Champion Dogs</label>
              <input
                type="number"
                value={state.championDogs}
                onChange={handleNumberChange('championDogs')}
                min={0}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Champion Bitches</label>
              <input
                type="number"
                value={state.championBitches}
                onChange={handleNumberChange('championBitches')}
                min={0}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base"
              />
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="bg-white rounded-xl shadow-lg">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-xl px-4 sm:px-6 py-3 sm:py-4">
            <h2 className="text-lg sm:text-xl font-bold">{state.selectedBreed || 'Breed'} Points Calculation</h2>
            {state.selectedState && <p className="text-purple-100 text-sm">{state.selectedState} • Division {state.divisionNumber}</p>}
          </div>
          
          <div className="p-4 sm:p-6">
            {state.calculationResults && (
              <>
                {/* Competition Summary */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Competition Summary</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-700">{state.calculationResults.competition.classDogs}</div>
                      <div className="text-xs text-gray-500">Class Dogs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-700">{state.calculationResults.competition.classBitches}</div>
                      <div className="text-xs text-gray-500">Class Bitches</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-700">{state.calculationResults.competition.championDogs}</div>
                      <div className="text-xs text-gray-500">Champion Dogs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-700">{state.calculationResults.competition.championBitches}</div>
                      <div className="text-xs text-gray-500">Champion Bitches</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                    <span className="text-sm text-gray-600">Total Competing: </span>
                    <span className="text-lg font-bold text-gray-800">{state.calculationResults.competition.totalCompeting}</span>
                  </div>
                </div>

                {/* Championship Points Section */}
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-xl px-6 py-4">
                    <h3 className="text-xl font-bold flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      Championship Points
                    </h3>
                    <p className="text-purple-100 text-sm mt-1">For Class Dogs seeking Champion status</p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-b-xl p-6">
                    {/* Winners Awards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <div className="text-xs font-medium text-purple-600 mb-1">
                          {state.calculationResults.championship.WD.label}
                        </div>
                        <div className="text-3xl font-bold text-purple-700">
                          {state.calculationResults.championship.WD.points}
                          {state.calculationResults.championship.WD.points >= 3 && (
                            <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {state.calculationResults.championship.WD.description}
                        </div>
                      </div>
                      
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <div className="text-xs font-medium text-purple-600 mb-1">
                          {state.calculationResults.championship.WB.label}
                        </div>
                        <div className="text-3xl font-bold text-purple-700">
                          {state.calculationResults.championship.WB.points}
                          {state.calculationResults.championship.WB.points >= 3 && (
                            <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {state.calculationResults.championship.WB.description}
                        </div>
                      </div>
                      
                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                        <div className="text-xs font-medium text-indigo-600 mb-1">
                          {state.calculationResults.championship.BOW.label}
                        </div>
                        <div className="text-3xl font-bold text-indigo-700">
                          {state.calculationResults.championship.BOW.points}
                          {state.calculationResults.championship.BOW.points >= 3 && (
                            <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {state.calculationResults.championship.BOW.description}
                        </div>
                      </div>
                    </div>
                    
                    {/* BOB/BOS for Class Dogs */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Best of Breed Scenarios</h5>
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.championship.BOB.dog.label}</div>
                            <div className="text-2xl font-bold text-green-600">
                              {state.calculationResults.championship.BOB.dog.points} pts
                              {state.calculationResults.championship.BOB.dog.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.championship.BOB.dog.description}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.championship.BOB.bitch.label}</div>
                            <div className="text-2xl font-bold text-green-600">
                              {state.calculationResults.championship.BOB.bitch.points} pts
                              {state.calculationResults.championship.BOB.bitch.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.championship.BOB.bitch.description}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Best Opposite Sex Scenarios</h5>
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.championship.BOS.dog.label}</div>
                            <div className="text-2xl font-bold text-blue-600">
                              {state.calculationResults.championship.BOS.dog.points} pts
                              {state.calculationResults.championship.BOS.dog.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.championship.BOS.dog.description}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.championship.BOS.bitch.label}</div>
                            <div className="text-2xl font-bold text-blue-600">
                              {state.calculationResults.championship.BOS.bitch.points} pts
                              {state.calculationResults.championship.BOS.bitch.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.championship.BOS.bitch.description}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Grand Champion Points Section */}
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-t-xl px-6 py-4">
                    <h3 className="text-xl font-bold flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H10a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd"/>
                      </svg>
                      Grand Champion Points
                    </h3>
                    <p className="text-amber-100 text-sm mt-1">For Champion Dogs seeking Grand Champion status</p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-b-xl p-6">
                    {/* BOB for Champions */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="text-xs font-medium text-green-600 mb-1">
                          {state.calculationResults.grandChampion.BOB.dog.label}
                        </div>
                        <div className="text-3xl font-bold text-green-700">
                          {state.calculationResults.grandChampion.BOB.dog.points}
                          {state.calculationResults.grandChampion.BOB.dog.points >= 3 && (
                            <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {state.calculationResults.grandChampion.BOB.dog.description}
                        </div>
                      </div>
                      
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="text-xs font-medium text-green-600 mb-1">
                          {state.calculationResults.grandChampion.BOB.bitch.label}
                        </div>
                        <div className="text-3xl font-bold text-green-700">
                          {state.calculationResults.grandChampion.BOB.bitch.points}
                          {state.calculationResults.grandChampion.BOB.bitch.points >= 3 && (
                            <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {state.calculationResults.grandChampion.BOB.bitch.description}
                        </div>
                      </div>
                    </div>
                    
                    {/* BOS and Select */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Best Opposite Sex</h5>
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.grandChampion.BOS.dog.label}</div>
                            <div className="text-2xl font-bold text-blue-600">
                              {state.calculationResults.grandChampion.BOS.dog.points} pts
                              {state.calculationResults.grandChampion.BOS.dog.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.grandChampion.BOS.dog.description}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.grandChampion.BOS.bitch.label}</div>
                            <div className="text-2xl font-bold text-blue-600">
                              {state.calculationResults.grandChampion.BOS.bitch.points} pts
                              {state.calculationResults.grandChampion.BOS.bitch.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.grandChampion.BOS.bitch.description}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Select Awards</h5>
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.grandChampion.select.dog.label}</div>
                            <div className="text-2xl font-bold text-orange-600">
                              {state.calculationResults.grandChampion.select.dog.points} pts
                              {state.calculationResults.grandChampion.select.dog.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.grandChampion.select.dog.description}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">{state.calculationResults.grandChampion.select.bitch.label}</div>
                            <div className="text-2xl font-bold text-orange-600">
                              {state.calculationResults.grandChampion.select.bitch.points} pts
                              {state.calculationResults.grandChampion.select.bitch.points >= 3 && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">MAJOR</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{state.calculationResults.grandChampion.select.bitch.description}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Card */}
                <InfoCard title="Understanding AKC Points">
                  <div className="space-y-4 text-sm">
                    <div className="border-l-4 border-purple-500 pl-4">
                      <strong className="text-purple-700">Championship Points (for Class Dogs):</strong>
                      <ul className="mt-1 text-gray-600 space-y-1">
                        <li>• WD/WB: Points based on class dogs/bitches only</li>
                        <li>• BOB: Class dogs of winner's sex + ALL champions</li>
                        <li>• BOS: Class dogs + champions of SAME SEX only</li>
                        <li>• BOW: Higher of WD or WB (special 1-pt rule if neither qualifies)</li>
                        <li>• Veterans and non-regular dogs NOT counted</li>
                      </ul>
                    </div>
                    
                    <div className="border-l-4 border-amber-500 pl-4">
                      <strong className="text-amber-700">Grand Champion Points (for Champions):</strong>
                      <ul className="mt-1 text-gray-600 space-y-1">
                        <li>• BOB: Higher of total competition OR BOS for their sex</li>
                        <li>• Select Dog/Bitch: Total of same sex minus 1</li>
                        <li>• GCH Points: BOB points + defeated champions</li>
                        <li>• Veterans ARE counted (effective 11/1/17)</li>
                      </ul>
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-800 font-medium">
                        <strong>AKC Rules:</strong> Maximum 5 points per show. Points ≥3 are majors.
                      </p>
                      <p className="text-blue-700 text-xs mt-1">
                        Championship requires 15 points with 2 majors. Grand Championship requires 25 points with 3 majors.
                      </p>
                    </div>
                  </div>
                </InfoCard>
              </>
            )}
            
            {!state.calculationResults && !state.error && state.validationErrors.length === 0 && (
              <div className="p-8 sm:p-12 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-lg sm:text-xl text-gray-500 font-medium">Enter show details to calculate points</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointsCalculator;